import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Container, ErrorState, LoadingState, PageHeader, Section } from '@/components/common';
import { routes } from '@/config/routes';
import { mapPublicProductSummaryToProductCardModel } from '@/features/catalog/api/catalogMappers';
import type { CatalogError } from '@/features/catalog/api/types';
import { CatalogControls, CatalogLoadMore, ProductGrid } from '@/features/catalog/components';
import { useCatalog } from '@/features/catalog/context';
import { presentationForCategory } from '@/features/catalog/data/categoryPresentation';
import { useCategoryProducts, usePublicCategory } from '@/features/catalog/hooks';
import { parseCatalogUrlState, toProductListInput } from '@/features/catalog/utils/catalogUrlState';
import { useDocumentMetadata } from '@/hooks/useDocumentMetadata';

import { ProductNotFoundPage } from './ProductNotFoundPage';

export function CategoryPage() {
  const { categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const state = parseCatalogUrlState(searchParams);
  const input = toProductListInput(state);
  const categoryInput = {
    ...(input.productType ? { productType: input.productType } : {}),
    ...(input.featured ? { featured: input.featured } : {}),
    ...(input.badge ? { badge: input.badge } : {}),
    ...(input.sort ? { sort: input.sort } : {}),
    ...(input.limit ? { limit: input.limit } : {}),
  };
  const { media } = useCatalog();
  const categoryQuery = usePublicCategory(categorySlug);
  const productsQuery = useCategoryProducts(categorySlug, categoryInput);
  const category = categoryQuery.data;
  const presentation = category ? presentationForCategory(category.slug, category.name) : undefined;
  const products = useMemo(() => {
    const matching = (productsQuery.data?.pages ?? [])
      .flatMap((page) => page.items)
      .filter((product) => product.categorySlug === categorySlug);
    return [
      ...new Map(
        matching.map((product) => [
          product.id,
          mapPublicProductSummaryToProductCardModel(product, media),
        ]),
      ).values(),
    ];
  }, [categorySlug, media, productsQuery.data]);
  useDocumentMetadata(
    category?.seo.title ?? category?.name ?? 'Product Category',
    category?.seo.description ?? category?.shortDescription,
  );

  if (categoryQuery.isPending) {
    return (
      <Section spacing="large">
        <Container size="wide" aria-busy="true">
          <LoadingState label="Loading product category" />
        </Container>
      </Section>
    );
  }
  if ((categoryQuery.error as CatalogError | null)?.code === 'category-not-found')
    return <ProductNotFoundPage categoryOnly />;
  if (categoryQuery.isError || !category || !presentation) {
    return (
      <Section spacing="large">
        <Container>
          <ErrorState
            title="This category could not be loaded"
            description="The category may still exist. Please retry the catalog connection."
            onRetry={() => void categoryQuery.refetch()}
          />
        </Container>
      </Section>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={presentation.eyebrow}
        title={category.name}
        description={category.shortDescription || presentation.heroMessage}
        breadcrumbs={[
          { label: 'Home', to: routes.home },
          { label: 'Products', to: routes.products },
          { label: presentation.shortTitle },
        ]}
      />
      <Section spacing="compact">
        <Container>
          <div className="category-intro">
            <i className={presentation.icon} aria-hidden="true" />
            <p>{category.shortDescription}</p>
          </div>
        </Container>
      </Section>
      <Section spacing="large">
        <Container size="wide" aria-busy={productsQuery.isPending}>
          <CatalogControls
            resultCount={products.length}
            fixedCategory={category.slug}
            updating={productsQuery.isFetching && !productsQuery.isPending}
          />
          {productsQuery.isPending && <LoadingState />}
          {productsQuery.isError && products.length === 0 && (
            <ErrorState
              title="Category products could not be loaded"
              onRetry={() => void productsQuery.refetch()}
            />
          )}
          {products.length > 0 && <ProductGrid products={products} />}
          {productsQuery.isSuccess && products.length === 0 && (
            <ProductGrid products={[]} emptyTitle="No products are published in this category" />
          )}
          <CatalogLoadMore
            hasMore={Boolean(productsQuery.hasNextPage)}
            loading={productsQuery.isFetchingNextPage}
            onLoadMore={() => void productsQuery.fetchNextPage()}
          />
        </Container>
      </Section>
      <Section tone="soft" spacing="large">
        <Container>
          <div className="customization-notes">
            <div>
              <p className="eyebrow">Personalization preview</p>
              <h2>Make it unmistakably yours</h2>
              <p>Available personalization is defined by each published product.</p>
            </div>
            <ul>
              {presentation.customizationNotes.map((note) => (
                <li key={note}>
                  <i className="pi pi-check" aria-hidden="true" />
                  {note}
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </Section>
    </>
  );
}
